/**
 * Puzzly Auth Service — Phase 1 (Auth Foundation).
 *
 * All auth operations funnel through this module. Exports are additive across
 * waves:
 *   - Wave 1 (this file initial drop): bootstrapAnonymous, subscribeToAuthState,
 *     getCurrentUser, friendlyAuthError (stub)
 *   - Wave 2 adds: ensureUserProfile
 *   - Wave 3 adds: signInWithGoogle, linkAnonymousToGoogle, sendMagicLink,
 *     completeMagicLinkIfPresent
 *   - Wave 4 adds: signOutUser, migrateLocalHistoryToUser
 *
 * PATTERNS §2: never export the raw `auth` object from here; callers must go
 * through these named exports so we can add logging, timeouts, and error
 * translation in one place.
 *
 * Logging convention (CLAUDE.md): every path emits `logger.info` on entry and
 * `logger.error(..., { error, code })` on catch.
 */

import { auth, db, functions, googleProvider } from "./firebase";
import { httpsCallable } from "firebase/functions";
import {
  signInAnonymously,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  linkWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  OAuthProvider,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  EmailAuthProvider,
  linkWithCredential,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  writeBatch,
  collection,
  getDocs,
} from "firebase/firestore";
import { Capacitor } from "@capacitor/core";
import logger from "./logger";
import i18n from "../i18n";
import {
  loadPuzzleHistory,
  markHistoryMigrated,
} from "./storage";
import { publicBaseUrl } from "./baseUrl";
import { withExternalFlow } from "../features/themes/externalFlow";

/**
 * Create or reuse an anonymous Firebase user.
 *
 * Idempotent: if `auth.currentUser` is already an anonymous user we return
 * that same user rather than minting a new uid. This prevents accidental
 * duplicate anon accounts when App.jsx's effects race.
 *
 * @returns {Promise<User>} The anonymous Firebase User.
 */
export async function bootstrapAnonymous() {
  try {
    if (auth.currentUser && auth.currentUser.isAnonymous) {
      logger.info("Anonymous bootstrap — reusing existing anon user", {
        uid: auth.currentUser.uid,
      });
      return auth.currentUser;
    }
    logger.info("Anonymous bootstrap — signing in anonymously");
    const cred = await signInAnonymously(auth);
    logger.info("Anonymous user created", { uid: cred.user.uid });
    return cred.user;
  } catch (err) {
    logger.error("Anonymous bootstrap failed", {
      error: err.message,
      code: err.code,
    });
    throw err;
  }
}

/**
 * Subscribe to auth state changes. Wrapper over `onAuthStateChanged` so
 * callers never touch the raw `auth` object (PATTERNS §2).
 *
 * @param {(user: import("firebase/auth").User | null) => void} callback
 * @returns {() => void} Unsubscribe function.
 */
export function subscribeToAuthState(callback) {
  logger.info("Subscribing to auth state");
  return onAuthStateChanged(auth, callback);
}

/**
 * Return the currently signed-in user (may be null before bootstrap completes).
 * Prefer `subscribeToAuthState` for reactive updates — this is a convenience
 * for imperative code paths (e.g. createPuzzle wanting to stamp `ownerUid`).
 *
 * @returns {import("firebase/auth").User | null}
 */
export function getCurrentUser() {
  return auth.currentUser;
}

/**
 * Ensure a `users/{uid}` profile document exists for the supplied Firebase user.
 *
 * Phase 1 / AUTH-07 / Pillar 3 — every authed user (anon or permanent) must
 * have a profile doc so cross-device restore (W4) and entitlements gating
 * (Phase 2) can read state from a single canonical place.
 *
 * Behavior:
 *   - First call for a uid creates `users/{uid}` with the canonical shape:
 *       { version:1, uid, isAnonymous, email, displayName, createdAt:serverTimestamp,
 *         tier:'free', entitlements:{ pro:false, source:null } }
 *   - Subsequent calls are no-ops: read existing doc, return its data, do NOT
 *     re-setDoc (preserves `createdAt` set-once contract — T-01-10).
 *   - Read wrapped in 10s timeout, write wrapped in 15s timeout (matches
 *     puzzleService pattern — guards against the "Firestore not enabled"
 *     hang.)
 *   - On failure logs `error: err.message` + `code: err.code` and rethrows.
 *
 * Field rationale (per locked decision AUTH-07):
 *   - `entitlements` is stamped as `{ pro: false, source: null }` here so the
 *     immutable-from-client guard in firestore.rules (W2.T2) can enforce that
 *     clients never *create* a doc with `entitlements.pro: true`.
 *
 * Phase 7 / 07-02 extension:
 *   - On CREATE we seed `activePuzzleCount: 0` so the Phase 7 slot Rules can
 *     read a non-null count on the very first puzzle write.
 *   - purchasedExtraSlots is intentionally omitted until the pack ships;
 *     Firestore Rules use safe-get to default the absent field to 0.
 *   - The idempotent branch is untouched — existing docs without activePuzzleCount
 *     are handled by the Rules safe-get pattern (RESEARCH Pitfall 1).
 *     This preserves the Phase 1 `createdAt` set-once contract (T-01-10).
 *
 * @param {import("firebase/auth").User} user — the authed Firebase user.
 * @returns {Promise<object>} the profile doc data (newly created or pre-existing).
 */
export async function ensureUserProfile(user) {
  if (!user || !user.uid) {
    const err = new Error("ensureUserProfile requires an authed user");
    logger.error("ensureUserProfile called without user", { error: err.message });
    throw err;
  }
  const uid = user.uid;
  logger.info("ensureUserProfile — checking profile", { uid });
  const profileRef = doc(db, "users", uid);

  let snap;
  try {
    snap = await Promise.race([
      getDoc(profileRef),
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("ensureUserProfile read timed out")),
          10000
        )
      ),
    ]);
  } catch (err) {
    logger.error("ensureUserProfile read failed", {
      uid,
      error: err.message,
      code: err.code,
    });
    throw err;
  }

  if (snap.exists()) {
    logger.info("ensureUserProfile — profile already exists, no-op", { uid });
    return snap.data();
  }

  const profile = {
    version: 1,
    uid,
    isAnonymous: !!user.isAnonymous,
    email: user.email || null,
    displayName: user.displayName || null,
    createdAt: serverTimestamp(),
    tier: "free",
    entitlements: { pro: false, source: null },
    // Phase 7 / 07-02 — slot counter seeded at 0. purchasedExtraSlots omitted
    // until pack ships; Rules use safe-get to default it to 0.
    activePuzzleCount: 0,
  };

  try {
    await Promise.race([
      setDoc(profileRef, profile),
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("ensureUserProfile write timed out")),
          15000
        )
      ),
    ]);
    logger.info("ensureUserProfile — profile created", { uid });
  } catch (err) {
    logger.error("ensureUserProfile write failed", {
      uid,
      error: err.message,
      code: err.code,
    });
    throw err;
  }

  // Re-read so callers receive the resolved server timestamp (not the sentinel).
  try {
    const freshSnap = await Promise.race([
      getDoc(profileRef),
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("ensureUserProfile re-read timed out")),
          10000
        )
      ),
    ]);
    if (freshSnap.exists()) {
      return freshSnap.data();
    }
  } catch (err) {
    // Re-read failure is non-fatal — return the in-memory profile.
    logger.warn("ensureUserProfile re-read failed (non-fatal)", {
      uid,
      error: err.message,
    });
  }

  return profile;
}

/**
 * Migrate the local puzzle history (Capacitor Preferences) into the
 * `users/{uid}/puzzles/*` Firestore subcollection.
 *
 * Phase 1 / W4.T2 / AUTH-05 — when an anon user upgrades to a permanent
 * account (Google popup, magic link, or `credential-already-in-use` recovery),
 * their local puzzle list MUST follow them to Firestore so cross-device
 * restore (W4.T5) can pick it up on a fresh device.
 *
 * Behavior:
 *   - Always re-runs (NO per-uid short-circuit). Re-migration is idempotent
 *     because docs are keyed by puzzleId, so signing in repeatedly on the same
 *     device just overwrites existing mirror docs in place. The old short-
 *     circuit caused the identity-fragmentation bug: puzzles created in a fresh
 *     anon session never reached an already-migrated account's mirror.
 *   - Reads local history via `loadPuzzleHistory()`.
 *   - Writes one doc per puzzle to `users/{uid}/puzzles/{puzzleId}` via
 *     `writeBatch` (single batch — Phase 1 assumes <100 local entries per
 *     Assumption A6; Firestore batch limit is 500 ops).
 *   - Wraps `batch.commit()` in a 30s timeout (RESEARCH §timeout pattern).
 *   - On success: flips `markHistoryMigrated(uid)`. Does NOT clear the local
 *     Capacitor Preferences cache (kept as offline cache per anti-pattern).
 *   - All failures are logged and swallowed — migration is best-effort, must
 *     NEVER block the auth happy path.
 *
 * @param {string} uid
 * @returns {Promise<void>}
 */
export async function migrateLocalHistoryToUser(uid) {
  if (!uid) {
    logger.warn("migrateLocalHistoryToUser called without uid — skipping");
    return;
  }

  try {
    // NB: deliberately NO `isHistoryMigrated` short-circuit. The old per-uid
    // flag skipped the WHOLE migration on any later sign-in, so a puzzle
    // created in a fresh anon session before re-signing into an
    // already-migrated account never reached `users/{uid}/puzzles` →
    // "previous puzzle not found" (identity-fragmentation bug). We always
    // re-run; `batch.set` keyed by puzzleId makes repeat migration idempotent
    // (already-mirrored puzzles are simply overwritten in place, no dupes).
    const local = await loadPuzzleHistory();
    if (!Array.isArray(local) || local.length === 0) {
      logger.info("migrateLocalHistoryToUser — no local entries to migrate", {
        uid,
      });
      // Still flip the flag so we don't re-check on every sign-in.
      await markHistoryMigrated(uid);
      return;
    }

    logger.info("migrateLocalHistoryToUser — migrating local entries", {
      uid,
      count: local.length,
    });

    const batch = writeBatch(db);
    let written = 0;
    for (const entry of local) {
      // Defensive: skip malformed entries lacking a puzzleId (can't key the doc).
      if (!entry || !entry.puzzleId) continue;
      const ref = doc(db, "users", uid, "puzzles", entry.puzzleId);
      batch.set(ref, entry);
      written += 1;
    }

    if (written === 0) {
      logger.warn(
        "migrateLocalHistoryToUser — no valid puzzleId entries, skipping commit",
        { uid }
      );
      await markHistoryMigrated(uid);
      return;
    }

    await Promise.race([
      batch.commit(),
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("migrateLocalHistoryToUser commit timed out")),
          30000
        )
      ),
    ]);

    await markHistoryMigrated(uid);
    logger.info("migrateLocalHistoryToUser — migration complete", {
      uid,
      written,
    });
  } catch (err) {
    logger.error("migrateLocalHistoryToUser failed", {
      uid,
      error: err.message,
      code: err.code,
    });
    // Swallow — auth happy path must continue. Migration retries on next
    // sign-in (the flag is only flipped on success).
  }
}

/**
 * Mirror a single just-created puzzle into `users/{uid}/puzzles/{puzzleId}`.
 *
 * Fixes the "puzzle-lost-on-signout" bug: `createPuzzle` only ever wrote the
 * canonical `puzzles/{id}` doc + the device-local cache — never the account
 * mirror. A puzzle created while ALREADY signed in therefore never reached
 * `users/{uid}/puzzles`; sign-out wipes the local cache; the next sign-in
 * reads the (empty-of-it) mirror via `loadUserPuzzleHistory` → "not found".
 * Writing the mirror entry at creation time makes signed-in creations durable
 * across sign-out/sign-in and across devices.
 *
 * Call this for PERMANENT users only (anon mirrors are never read — anon
 * puzzles ride the local cache + sign-in migration instead). Keyed by
 * puzzleId so re-writes are idempotent. Best-effort: errors are logged and
 * swallowed — never block the publish happy path.
 *
 * @param {string} uid
 * @param {object} entry — the createdPuzzles history entry
 *   ({ id, puzzleId, title, theme, levelCount, createdAt, shareUrl })
 * @returns {Promise<void>}
 */
export async function addPuzzleToUserHistory(uid, entry) {
  if (!uid || !entry || !entry.puzzleId) {
    logger.warn("addPuzzleToUserHistory — missing uid or entry.puzzleId, skipping", {
      hasUid: !!uid,
      hasPuzzleId: !!(entry && entry.puzzleId),
    });
    return;
  }
  try {
    const ref = doc(db, "users", uid, "puzzles", entry.puzzleId);
    await Promise.race([
      setDoc(ref, entry),
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("addPuzzleToUserHistory write timed out")),
          15000
        )
      ),
    ]);
    logger.info("addPuzzleToUserHistory — mirrored puzzle to account", {
      uid,
      puzzleId: entry.puzzleId,
    });
  } catch (err) {
    logger.error("addPuzzleToUserHistory failed", {
      uid,
      puzzleId: entry.puzzleId,
      error: err.message,
      code: err.code,
    });
    // Swallow — publish must never fail because the mirror write hiccupped.
    // The puzzle still exists (canonical doc + local cache); a later sign-in
    // migration re-mirrors from local if needed.
  }
}

/**
 * Load the user's puzzle history mirror from Firestore (`users/{uid}/puzzles/*`).
 *
 * Phase 1 / W4.T5 / AUTH-06 — used by App.jsx's onAuthStateChanged callback
 * when a permanent (non-anon) user signs in. Returned entries are merged into
 * `createdPuzzles` state so a fresh device sees the user's full history.
 *
 * Returns an array shaped identically to local `createdPuzzles` entries:
 *   { id, puzzleId, title, theme, levelCount, createdAt, shareUrl }
 *
 * Wrapped in a 10s timeout. Errors are logged and surfaced as an empty array
 * (cross-device restore is a nice-to-have; never block sign-in on it).
 *
 * @param {string} uid
 * @returns {Promise<Array<object>>}
 */
export async function loadUserPuzzleHistory(uid) {
  if (!uid) {
    logger.warn("loadUserPuzzleHistory called without uid — returning []");
    return [];
  }
  logger.info("loadUserPuzzleHistory entry", { uid });
  try {
    const snap = await Promise.race([
      getDocs(collection(db, "users", uid, "puzzles")),
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("loadUserPuzzleHistory read timed out")),
          10000
        )
      ),
    ]);
    const entries = snap.docs.map((d) => d.data());
    logger.info("loadUserPuzzleHistory — fetched", {
      uid,
      count: entries.length,
    });
    return entries;
  } catch (err) {
    logger.error("loadUserPuzzleHistory failed", {
      uid,
      error: err.message,
      code: err.code,
    });
    return [];
  }
}

/**
 * Sign the current user out. The next mount of App.jsx's auth-bootstrap
 * effect (W1.T3) will silently re-bootstrap a fresh anon session — per
 * RESEARCH Open Question #3 RESOLVED. No splash kick-out, no auth-required
 * screen.
 *
 * Wrapped with logger.info before/after + try/catch so callers can fire and
 * forget without worrying about unhandled rejections.
 *
 * @returns {Promise<void>}
 */
export async function signOutUser() {
  const uidBefore = auth.currentUser ? auth.currentUser.uid : null;
  logger.info("signOutUser entry", { uid: uidBefore });
  try {
    await signOut(auth);
    logger.info("signOutUser — signed out", { uidBefore });
  } catch (err) {
    logger.error("signOutUser failed", {
      uidBefore,
      error: err.message,
      code: err.code,
    });
    throw err;
  }
}

/**
 * Sign in (or link, if already anon) with Google.
 *
 * Behavior matrix (RESEARCH.md §Pattern 2 + locked decision #3):
 *   - On Capacitor native (`Capacitor.isNativePlatform()` truthy): the OS
 *     shows the Google sheet (`FirebaseAuthentication.signInWithGoogle` with
 *     `skipNativeAuth`), and its idToken becomes a Firebase credential:
 *       - anon current user → `linkWithCredential(anonUser, cred)`
 *       - no current user   → `signInWithCredential(auth, cred)`
 *     Google refuses OAuth from embedded webviews, so the redirect path that
 *     used to live here could never work inside the app.
 *   - On web (popup-capable):
 *       - anon current user → `linkWithPopup(anonUser, googleProvider)`
 *       - no current user   → `signInWithPopup(auth, googleProvider)`
 *
 * Error handling:
 *   - `auth/credential-already-in-use` (anon link path only) → extract pending
 *     credential via `GoogleAuthProvider.credentialFromError`, sign in with it
 *     directly, ALWAYS call `migrateLocalHistoryToUser(result.user.uid)`
 *     afterward so the anon's local puzzles follow the user. (Pitfall #3)
 *   - `auth/popup-blocked` / `auth/popup-closed-by-user` → throw a
 *     friendly error (`friendlyAuthError`).
 *   - Any other code → log + rethrow.
 *
 * On any successful sign-in (popup, redirect-result, fallback) we call
 * `ensureUserProfile(user)` then `migrateLocalHistoryToUser(user.uid)` so the
 * canonical Firestore profile + history exist before the caller proceeds.
 *
 * @returns {Promise<import("firebase/auth").User | undefined>}
 */
export async function signInWithGoogle() {
  const native = isCapacitorNative();
  const anonUser =
    auth.currentUser && auth.currentUser.isAnonymous
      ? auth.currentUser
      : null;
  logger.info("signInWithGoogle entry", {
    native,
    anonUid: anonUser ? anonUser.uid : null,
  });

  // ── Capacitor native: the OS runs the OAuth, the JS SDK gets the result ──
  let nativeCred = null;
  if (native) {
    try {
      const { FirebaseAuthentication } = await import(
        "@capacitor-firebase/authentication"
      );
      // Foglio di Google: flusso esterno, l'icona stagionale non cambia sotto (Task 11).
      const res = await withExternalFlow(() =>
        FirebaseAuthentication.signInWithGoogle({ skipNativeAuth: true })
      );
      const idToken = res && res.credential ? res.credential.idToken : null;
      if (!idToken) {
        throw new Error("Google non ha restituito un idToken");
      }
      nativeCred = GoogleAuthProvider.credential(idToken);
    } catch (err) {
      logger.error("signInWithGoogle native sheet failed", {
        error: err.message,
        code: err.code,
      });
      throw new Error(friendlyAuthError(err));
    }
  }

  try {
    let result;
    if (native) {
      result = anonUser
        ? await linkWithCredential(anonUser, nativeCred)
        : await signInWithCredential(auth, nativeCred);
    } else if (anonUser) {
      result = await linkWithPopup(anonUser, googleProvider);
    } else {
      result = await signInWithPopup(auth, googleProvider);
    }
    const user = result.user;
    logger.info("signInWithGoogle success", { uid: user.uid });
    // Re-import via module ref so jest.mock partial overrides take effect.
    const self = require("./authService");
    await self.ensureUserProfile(user);
    await self.migrateLocalHistoryToUser(user.uid);
    return user;
  } catch (err) {
    if (
      (err.code === "auth/credential-already-in-use" ||
        err.code === "auth/email-already-in-use") &&
      anonUser
    ) {
      // Conflict recovery (Pitfall #3 + W4 patch 2 Defect 1):
      //   - `auth/credential-already-in-use`: the Google credential already
      //     maps to a separate Firebase user.
      //   - `auth/email-already-in-use`: the Google account's email is
      //     already attached to a different Firebase user (typically one
      //     created via magic-link sign-in with the same email).
      // Both route to identical recovery: sign into THAT user, then migrate
      // the anon's local puzzles into the recovered account.
      logger.warn("signInWithGoogle conflict — falling back to existing account", {
        code: err.code,
      });
      const pendingCred =
        GoogleAuthProvider.credentialFromError(err) || nativeCred;
      const result = await signInWithCredential(auth, pendingCred);
      const user = result.user;
      logger.info("signInWithGoogle conflict — recovered existing account", {
        recoveredUid: user.uid,
        abandonedAnonUid: anonUser.uid,
      });
      const self = require("./authService");
      await self.ensureUserProfile(user);
      await self.migrateLocalHistoryToUser(user.uid);
      return user;
    }
    if (
      err.code === "auth/popup-blocked" ||
      err.code === "auth/popup-closed-by-user"
    ) {
      logger.warn("signInWithGoogle popup interrupted", { code: err.code });
      throw new Error(friendlyAuthError(err));
    }
    logger.error("signInWithGoogle failed", {
      error: err.message,
      code: err.code,
    });
    throw err;
  }
}

/**
 * Convenience export — internally `signInWithGoogle` already handles the anon
 * link path. Kept as a named export so callers (and Phase 6 Apple wiring) can
 * read intent from the call site.
 */
export const linkAnonymousToGoogle = signInWithGoogle;

/**
 * Sign in with Apple (#31). La Guideline 4.8 lo pretende su iOS accanto a
 * Google; sul web e su Android non lo offriamo, finché non esiste un Services
 * ID configurato in Firebase per il flusso OAuth via browser.
 */
export function appleSignInAvailable() {
  try {
    return Capacitor.getPlatform() === "ios";
  } catch (e) {
    return false;
  }
}

// L'email dell'Apple ID ha già un account con un altro accesso (di solito
// Google): il riquadro offre allora «Entra con Google e collega Apple».
export const APPLE_EMAIL_IN_USE = "puzzly/apple-email-in-use";

// Il foglio di Apple chiuso dall'utente arriva come AuthorizationError 1001.
function appleSheetCancelled(err) {
  return /1001|cancel/i.test((err && err.message) || "");
}

async function nativeAppleCredential() {
  const { FirebaseAuthentication } = await import(
    "@capacitor-firebase/authentication"
  );
  const res = await withExternalFlow(() =>
    FirebaseAuthentication.signInWithApple({ skipNativeAuth: true })
  );
  const c = res && res.credential;
  if (!c || !c.idToken || !c.nonce) {
    throw new Error("Apple non ha restituito idToken e nonce");
  }
  return c;
}

// Apre il foglio di Apple e ne fa una credenziale apple.com. Ogni credenziale
// vale una volta sola, quindi chi ne ha bisogno di nuovo riapre il foglio.
async function credenzialeAppleDalFoglio(dove) {
  try {
    const c = await nativeAppleCredential();
    return new OAuthProvider("apple.com").credential({ idToken: c.idToken, rawNonce: c.nonce });
  } catch (err) {
    logger.error(`${dove} native sheet failed`, { error: err.message });
    throw new Error(
      appleSheetCancelled(err)
        ? friendlyAuthError({ code: "auth/popup-closed-by-user" })
        : friendlyAuthError(err)
    );
  }
}

/**
 * Stesso percorso del ramo nativo di Google: il foglio di sistema dà idToken e
 * nonce grezzo, il JS SDK accede (o collega l'anonimo) con la credenziale.
 */
export async function signInWithApple() {
  const anonimo =
    auth.currentUser && auth.currentUser.isAnonymous ? auth.currentUser : null;
  // Si collega l'anonimo solo se ha puzzle da portarsi dietro. Altrimenti si
  // entra e basta: chi torna con Apple dopo essere uscito non ha niente da
  // collegare, e così vede il foglio una volta sola (21/10). Nel dubbio si collega.
  let anonUser = anonimo;
  if (anonimo) {
    try {
      const locali = await loadPuzzleHistory();
      if (!Array.isArray(locali) || locali.length === 0) anonUser = null;
    } catch (e) {
      anonUser = anonimo;
    }
  }
  logger.info("signInWithApple entry", { anonUid: anonimo ? anonimo.uid : null, collega: Boolean(anonUser) });

  const cred = await credenzialeAppleDalFoglio("signInWithApple");

  let result;
  try {
    result = anonUser
      ? await linkWithCredential(anonUser, cred)
      : await signInWithCredential(auth, cred);
  } catch (err) {
    // Il token di Apple vale una volta sola (nonce): dopo un collegamento fallito
    // si può entrare solo con la credenziale che Firebase rimanda nell'errore,
    // quella col pendingToken. Riusare `cred` dà «Duplicate credential received».
    const giaCollegato = err.code === "auth/credential-already-in-use" && anonUser;
    if (!giaCollegato) {
      logger.error("signInWithApple failed", { error: err.message, code: err.code });
      const emailGiaUsata =
        err.code === "auth/email-already-in-use" ||
        err.code === "auth/account-exists-with-different-credential";
      throw Object.assign(new Error(friendlyAuthError(err)), emailGiaUsata ? { code: APPLE_EMAIL_IN_USE } : {});
    }
    // L'identità Apple ha già un suo utente: si entra in quello e ci si portano
    // dietro i puzzle dell'anonimo. Con Apple Firebase di solito non rimanda il
    // pendingToken (visto il 21/10): il token è speso, quindi si riapre il foglio.
    const daErrore = OAuthProvider.credentialFromError(err);
    const riusabile = Boolean(daErrore && daErrore.pendingToken);
    logger.warn("signInWithApple conflict — falling back to existing account", {
      code: err.code,
      pendingToken: riusabile,
    });
    const accesso = riusabile ? daErrore : await credenzialeAppleDalFoglio("signInWithApple rientro");
    result = await signInWithCredential(auth, accesso);
  }
  const user = result.user;
  logger.info("signInWithApple success", { uid: user.uid });
  const self = require("./authService");
  await self.ensureUserProfile(user);
  await self.migrateLocalHistoryToUser(user.uid);
  return user;
}

/**
 * Collega Apple all'account in cui si è appena entrati (di solito con Google),
 * così la volta dopo «Continua con Apple» entra direttamente. Riapre il foglio di
 * Apple: il token del tentativo fallito è già speso.
 */
export async function linkAppleToCurrentUser() {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) {
    throw new Error(i18n.t("ui.authErrors.generic"));
  }

  const cred = await credenzialeAppleDalFoglio("linkAppleToCurrentUser");

  try {
    await linkWithCredential(user, cred);
  } catch (err) {
    if (err.code === "auth/provider-already-linked") return user;
    logger.error("linkAppleToCurrentUser failed", { error: err.message, code: err.code });
    throw new Error(friendlyAuthError(err));
  }
  logger.info("linkAppleToCurrentUser success", { uid: user.uid });
  return user;
}

/**
 * Apple chiede di revocare i token quando un account creato con Sign in with
 * Apple viene cancellato. Serve un authorization code fresco, quindi si riapre
 * il foglio di Apple; lo scambio e la revoca li fa Firebase con la chiave .p8
 * configurata nel provider Apple. Stessa richiesta che manda l'SDK iOS di
 * Firebase (`revokeToken(withAuthorizationCode:)`): con `skipNativeAuth` la
 * sessione nativa non esiste, e quel metodo non farebbe niente.
 *
 * Non fa nulla se l'utente non ha Apple fra i provider o se non siamo su iOS.
 */
export async function revokeAppleSignIn() {
  const user = auth.currentUser;
  const hasApple =
    user && (user.providerData || []).some((p) => p.providerId === "apple.com");
  if (!hasApple || !appleSignInAvailable()) return false;

  const c = await nativeAppleCredential();
  if (!c.authorizationCode) {
    throw new Error("Apple non ha restituito un authorization code");
  }
  const res = await fetch(
    `https://identitytoolkit.googleapis.com/v2/accounts:revokeToken?key=${auth.app.options.apiKey}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        providerId: "apple.com",
        tokenType: "CODE",
        token: c.authorizationCode,
        idToken: await user.getIdToken(),
      }),
    }
  );
  if (!res.ok) throw new Error(`revokeToken: HTTP ${res.status}`);
  logger.info("revokeAppleSignIn: token Apple revocato", { uid: user.uid });
  return true;
}

// localStorage keys (cross-tab return — anti-pattern §5 forbids sessionStorage).
const MAGIC_LINK_EMAIL_KEY = "puzzly_magic_email";
const MAGIC_LINK_ANON_UID_KEY = "puzzly_magic_anon_uid";

/**
 * Send a sign-in email-link to `email`. The email is Puzzly's own (branded,
 * in the app language), sent by the `sendSignInLink` Cloud Function; on a
 * server-side failure we fall back to Firebase's stock email, localised via
 * `auth.languageCode`. ANY refusal from the function (rate limit, missing anon
 * session, unlisted origin, outage) falls back: the Firebase endpoint has its own
 * quotas and authorized-domain checks, so sign-in never gets worse than before.
 * Stash the email (and the anon uid, if the current user is anonymous) in
 * localStorage so we can complete the sign-in when the user returns via the link.
 *
 * Action code settings: `url` is the current page + `?magic=1` so the return
 * URL hits the same SPA which then runs `completeMagicLinkIfPresent` on
 * mount. `handleCodeInApp: true` is required for client-side link completion.
 *
 * Throws on failure — `auth/quota-exceeded` (Spark plan 5/day cap) is
 * translated by `friendlyAuthError` at the call site.
 *
 * @param {string} email
 * @returns {Promise<void>}
 */
export async function sendMagicLink(email) {
  logger.info("sendMagicLink entry", { email });
  const continueUrl = `${publicBaseUrl()}?magic=1`;
  const actionCodeSettings = { url: continueUrl, handleCodeInApp: true };
  const lang = String(i18n.resolvedLanguage || i18n.language || "en").slice(0, 2);
  try {
    try {
      await httpsCallable(functions, "sendSignInLink")({ email, lang, continueUrl });
      logger.info("sendMagicLink sent (Puzzly email)", { email });
    } catch (err) {
      logger.warn("sendMagicLink: Puzzly email failed, using Firebase email", { code: err && err.code });
      auth.languageCode = lang;
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      logger.info("sendMagicLink sent (Firebase fallback)", { email });
    }
    window.localStorage.setItem(MAGIC_LINK_EMAIL_KEY, email);
    if (auth.currentUser && auth.currentUser.isAnonymous) {
      window.localStorage.setItem(MAGIC_LINK_ANON_UID_KEY, auth.currentUser.uid);
    }
  } catch (err) {
    logger.error("sendMagicLink failed", { error: err.message, code: err.code });
    throw err;
  }
}

/**
 * If the current URL is a sign-in email link, complete the sign-in and return
 * the resulting user. Otherwise return null.
 *
 * Behavior matrix (RESEARCH.md §Pattern 3 + Pitfall #4):
 *   - Same-device return (anon uid in localStorage matches current anon user):
 *       link the email credential to the anon user via
 *       `linkWithCredential(EmailAuthProvider.credentialWithLink(...))` so the
 *       anon's history (puzzles, ownerUid) follows the user.
 *   - Cross-device return (no anon match, or anon mismatch):
 *       call `signInWithEmailLink` to authenticate as the email's permanent
 *       user. If the email isn't in localStorage (cross-tab/cross-device), we
 *       return a sentinel `{ status: "needs_email", url }` so the caller can
 *       render an in-app modal (EmailReentryModal) to collect the email and
 *       then call `completeMagicLinkWithEmail(email, url)`.
 *   - On successful sign-in: strip magic-link query params from the URL via
 *     `replaceState` so a refresh doesn't re-trigger the handler. Clear both
 *     localStorage keys. Call `ensureUserProfile(user)` +
 *     `migrateLocalHistoryToUser(user.uid)`.
 *   - When returning the `needs_email` sentinel: do NOT strip URL params or
 *     clear localStorage — the URL is needed by `completeMagicLinkWithEmail`
 *     and the helper itself will perform the cleanup after a successful sign-in.
 *
 * MUST run BEFORE the hash-URL puzzle detection effect in App.jsx (W3.T4).
 *
 * @returns {Promise<import("firebase/auth").User | null | { status: "needs_email", url: string }>}
 */
export async function completeMagicLinkIfPresent(url = window.location.href) {
  // Nell'app il link arriva da `appUrlOpen` (#67), non dall'indirizzo della pagina.
  if (!isSignInWithEmailLink(auth, url)) {
    return null;
  }
  logger.info("completeMagicLinkIfPresent — sign-in link detected");

  const email = window.localStorage.getItem(MAGIC_LINK_EMAIL_KEY);
  if (!email) {
    // Cross-device / cross-browser return — caller must collect the email via
    // EmailReentryModal then call `completeMagicLinkWithEmail(email, url)`.
    logger.info(
      "completeMagicLinkIfPresent — no stashed email, returning needs_email sentinel",
      { hasUrl: !!url }
    );
    return { status: "needs_email", url };
  }

  return finishMagicLinkSignIn(email, url);
}

/**
 * Complete a magic-link sign-in with an email collected out-of-band (e.g. via
 * EmailReentryModal on the cross-device return path). The caller is
 * responsible for the URL — typically captured at the moment
 * `completeMagicLinkIfPresent()` returned the `needs_email` sentinel.
 *
 * Same post-sign-in side effects as the happy path:
 *   - clear `puzzly_magic_email` + `puzzly_magic_anon_uid` localStorage keys
 *   - replaceState to strip magic-link query params from the URL
 *   - ensureUserProfile + migrateLocalHistoryToUser
 *
 * @param {string} email
 * @param {string} url   the full magic-link URL (must include oobCode + apiKey)
 * @returns {Promise<import("firebase/auth").User>}
 */
export async function completeMagicLinkWithEmail(email, url) {
  if (!email || !url) {
    const err = new Error(
      "completeMagicLinkWithEmail requires both email and url"
    );
    logger.error("completeMagicLinkWithEmail called without inputs", {
      hasEmail: !!email,
      hasUrl: !!url,
    });
    throw err;
  }
  logger.info("completeMagicLinkWithEmail entry");
  return finishMagicLinkSignIn(email, url);
}

/**
 * Internal helper — shared by `completeMagicLinkIfPresent` (same-device) and
 * `completeMagicLinkWithEmail` (cross-device modal callback). Signs the user
 * in (linking to anon if applicable), strips URL params, clears localStorage,
 * and runs ensureUserProfile + migrateLocalHistoryToUser.
 */
async function finishMagicLinkSignIn(email, url) {
  const anonUidBefore = window.localStorage.getItem(MAGIC_LINK_ANON_UID_KEY);
  const wasAnon =
    !!auth.currentUser &&
    auth.currentUser.isAnonymous &&
    auth.currentUser.uid === anonUidBefore;

  let user;
  try {
    // Come per Apple: si collega l'anonimo solo se ha puzzle da portarsi dietro.
    // Senza, si entra direttamente, così chi rientra non passa da un collegamento
    // che fallirebbe (21/10).
    let daCollegare = wasAnon;
    if (wasAnon) {
      try {
        const locali = await loadPuzzleHistory();
        if (!Array.isArray(locali) || locali.length === 0) daCollegare = false;
      } catch (e) {
        daCollegare = true;
      }
    }
    if (daCollegare) {
      const cred = EmailAuthProvider.credentialWithLink(email, url);
      try {
        const result = await linkWithCredential(auth.currentUser, cred);
        user = result.user;
        logger.info("finishMagicLinkSignIn — linked to anon", { uid: user.uid });
      } catch (err) {
        // L'email ha già un account e l'anonimo ha puzzle: il collegamento
        // fallisce, ma sul server vero il codice del link resta valido e porta
        // dritto in quell'account (provato il 21/10; l'emulatore invece lo
        // consuma, quindi lì questo ramo non si prova).
        if (err.code !== "auth/email-already-in-use" && err.code !== "auth/credential-already-in-use") throw err;
        logger.warn("finishMagicLinkSignIn — email già di un account, accesso diretto", { code: err.code });
        const result = await signInWithEmailLink(auth, email, url);
        user = result.user;
      }
    } else {
      const result = await signInWithEmailLink(auth, email, url);
      user = result.user;
      logger.info("finishMagicLinkSignIn — signed in", { uid: user.uid });
    }
  } catch (err) {
    logger.error("finishMagicLinkSignIn failed", {
      error: err.message,
      code: err.code,
    });
    // Clean up + strip params even on failure so a stale invalid link doesn't
    // re-trigger forever. Then rethrow.
    window.localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
    window.localStorage.removeItem(MAGIC_LINK_ANON_UID_KEY);
    try {
      window.history.replaceState(
        {},
        "",
        window.location.pathname + window.location.hash
      );
    } catch (e) {
      /* swallow — replaceState shim absent in some test envs */
    }
    throw err;
  }

  // Success path — clean up.
  window.localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
  window.localStorage.removeItem(MAGIC_LINK_ANON_UID_KEY);
  try {
    window.history.replaceState(
      {},
      "",
      window.location.pathname + window.location.hash
    );
  } catch (e) {
    /* swallow */
  }

  // Ensure profile + history migration. Re-import via module ref so jest.mock
  // partial overrides take effect in unit tests.
  try {
    const self = require("./authService");
    await self.ensureUserProfile(user);
    await self.migrateLocalHistoryToUser(user.uid);
  } catch (e) {
    logger.warn("finishMagicLinkSignIn — post-sign-in side effects failed", {
      uid: user.uid,
      error: e.message,
    });
    // Non-fatal — the user IS signed in.
  }

  return user;
}

/**
 * Translate a Firebase auth error into user-facing copy.
 *
 * Code → copy table covers the realistic Phase 1 surface (Google popup, magic
 * link, network). Anything else falls back to a generic "Something went wrong"
 * to avoid leaking internal codes.
 *
 * @param {Error & { code?: string }} err
 * @returns {string}
 */
export function friendlyAuthError(err) {
  if (!err) return i18n.t("ui.authErrors.generic");
  switch (err.code) {
    case "auth/popup-blocked":
      return i18n.t("ui.authErrors.popupBlocked");
    case "auth/popup-closed-by-user":
      return i18n.t("ui.authErrors.cancelled");
    case "auth/network-request-failed":
      return i18n.t("ui.authErrors.network");
    case "auth/quota-exceeded":
      return i18n.t("ui.authErrors.rateLimited");
    case "auth/invalid-action-code":
      return i18n.t("ui.authErrors.invalidLink");
    case "auth/expired-action-code":
      return i18n.t("ui.authErrors.expiredLink");
    case "auth/credential-already-in-use":
      // Internal — should be caught + recovered before surfacing to user.
      return i18n.t("ui.authErrors.alreadyInUse");
    case "auth/email-already-in-use":
    case "auth/account-exists-with-different-credential":
      return i18n.t("ui.authErrors.emailInUse");
    default:
      return err.message || i18n.t("ui.authErrors.generic");
  }
}

/**
 * Internal helper — wrapped so jest.mock can override `Capacitor` import via
 * the module-level mock without colliding with this function's identity.
 */
function isCapacitorNative() {
  try {
    return !!(Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform());
  } catch (e) {
    return false;
  }
}
